import * as React from 'react'
import { Boxes, Building2, ArrowLeftRight, AlertTriangle, DollarSign, Gauge } from 'lucide-react'
import type { StockOverviewResponse } from '@/lib/api-client'
import { formatCurrency, formatNumber } from '@/lib/utils'

interface Props {
  data: StockOverviewResponse | undefined
  isLoading: boolean
  demo: boolean
}

function Card({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: typeof Boxes
  label: string
  value: React.ReactNode
  hint?: string
  accent?: string
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-text-muted">{label}</span>
        <Icon className="w-4 h-4 text-text-muted" />
      </div>
      <div className={`text-2xl font-bold ${accent ?? 'text-text-dark'}`}>{value}</div>
      {hint && <div className="text-xs text-text-muted mt-1">{hint}</div>}
    </div>
  )
}

export function StockSummaryCards({ data, isLoading, demo }: Props) {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 h-[88px] animate-pulse" />
        ))}
      </div>
    )
  }
  const s = data.summary
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Card icon={Boxes} label="SKUs Moving" value={s.skusMoving} hint={`${formatNumber(s.totalUnits)} units`} />
      <Card icon={Building2} label="Branches" value={s.branches} hint="multi-branch" />
      <Card icon={ArrowLeftRight} label="Rebalance Ops" value={s.opportunities} accent="text-info" hint="≥3× imbalance" />
      <Card icon={AlertTriangle} label="Stockout Signals" value={s.stockouts} accent="text-warning" hint={`last ${data.window ? '6' : ''} months`} />
      <Card
        icon={DollarSign}
        label="Revenue Tracked"
        value={demo ? '— —' : formatCurrency(s.totalRevenue)}
        hint={demo ? 'hidden (privacy)' : `${data.window.windowDays}d window`}
        accent="text-success"
      />
      <Card icon={Gauge} label="Coverage Model" value={`${data.coverageDays}d`} hint="even-stocking est." />
    </div>
  )
}
